import {Fragment, useCallback, useEffect, useId, useMemo, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {DataTable, type Column} from '@/components/data-table'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tag} from '@/components/ui/tag'
import {cn} from '@/lib/utils'
import type {Fila} from '@/hooks/sgsst/riesgos/use-ipevr'

export type GroupMode = 'none' | 'cargo' | 'proceso' | 'area'
type Agrupacion = Exclude<GroupMode, 'none'>

// GTC 45: interpretación I y II es «no aceptable».
const NO_ACEPTABLE = new Set(['I', 'II'])

// Igual que la vista sin agrupar (`matriz-riesgos-grid.tsx`): cambiar de agrupación no
// cambia cuántas filas caben.
const PAGE_SIZE = 15
const SEVERITY: Record<string, number> = {I: 0, II: 1, III: 2, IV: 3}

const SIN_GRUPO: Record<Agrupacion, string> = {
  cargo: 'ipevr.agrupar.sinCargo',
  proceso: 'ipevr.agrupar.sinProceso',
  area: 'ipevr.agrupar.sinArea',
}

type MatrizGroup = {
  key: string
  name: string
  filas: Fila[]
  noAceptables: number
  expuestos: number
}

function worstFirst(filas: Fila[], noAceptablePrimero: boolean): Fila[] {
  const score = (f: Fila) => (f.nr ?? 0) * (f.num_expuestos ?? 1)
  const sorted = [...filas].sort((a, b) => score(b) - score(a) || a.descripcion.localeCompare(b.descripcion))
  if (noAceptablePrimero) {
    sorted.sort((a, b) => (SEVERITY[a.interpretacion ?? ''] ?? 4) - (SEVERITY[b.interpretacion ?? ''] ?? 4))
  }
  return sorted
}

// Una fila con varios cargos va al grupo de cada uno: los subtotales pueden sumar más que el total.
function gruposDeLaFila(
  fila: Fila,
  mode: Agrupacion,
  areaNameById: Map<string, string>
): {key: string; name: string | undefined}[] {
  if (mode === 'cargo') return fila.cargos.map((cargo) => ({key: `c:${cargo}`, name: cargo}))
  if (mode === 'proceso') {
    const proceso = fila.proceso?.trim()
    return proceso ? [{key: `p:${proceso}`, name: proceso}] : []
  }
  return fila.area_id ? [{key: fila.area_id, name: areaNameById.get(fila.area_id)}] : []
}

// Keeps the input order: each group appears where its worst fila does.
function buildGroups(
  filas: Fila[],
  mode: Agrupacion,
  areaNameById: Map<string, string>,
  fallback: string
): MatrizGroup[] {
  const byKey = new Map<string, MatrizGroup>()
  for (const fila of filas) {
    const grupos = gruposDeLaFila(fila, mode, areaNameById)
    for (const {key, name} of grupos.length ? grupos : [{key: '__none__', name: fallback}]) {
      let group = byKey.get(key)
      if (!group) {
        group = {key, name: name ?? fallback, filas: [], noAceptables: 0, expuestos: 0}
        byKey.set(key, group)
      }
      group.filas.push(fila)
      if (fila.interpretacion && NO_ACEPTABLE.has(fila.interpretacion)) group.noAceptables += 1
      group.expuestos += fila.num_expuestos ?? 0
    }
  }
  return [...byKey.values()]
}

function readCollapsed(storageKey: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(storageKey)
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set<string>()
  }
}

// Stores only the collapsed keys, so a brand-new group opens expanded.
function useCollapsedState(storageKey: string) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(storageKey))
  useEffect(() => {
    setCollapsed(readCollapsed(storageKey))
  }, [storageKey])
  const toggle = useCallback(
    (groupKey: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev)
        if (next.has(groupKey)) next.delete(groupKey)
        else next.add(groupKey)
        try {
          sessionStorage.setItem(storageKey, JSON.stringify([...next]))
        } catch {
          /* sessionStorage unavailable: keep the in-memory state only. */
        }
        return next
      })
    },
    [storageKey]
  )
  return {collapsed, toggle}
}

/** Cada grupo pagina el suyo (R6) para que el recorte no cruce un encabezado. La página se
 * acota a la última porque un filtro puede encoger el grupo por debajo de ella. */
function PanelDelGrupo({
  panelId,
  hidden,
  filas,
  columns,
  rowTestId,
  onRowClick,
  renderMobileCard,
}: {
  panelId: string
  hidden: boolean
  filas: Fila[]
  columns: Column<Fila>[]
  rowTestId: (fila: Fila) => string
  onRowClick: (fila: Fila) => void
  renderMobileCard: (fila: Fila) => ReactNode
}) {
  const [page, setPage] = useState(1)
  const total = filas.length
  const ultima = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const actual = Math.min(page, ultima)
  const visibles = total > PAGE_SIZE ? filas.slice((actual - 1) * PAGE_SIZE, actual * PAGE_SIZE) : filas
  return (
    <div id={panelId} hidden={hidden} className="pt-1 pb-2">
      <div className="hidden px-4 md:block">
        <DataTable
          columns={columns}
          rows={visibles}
          rowKey={(f) => f.id}
          rowTestId={rowTestId}
          onRowClick={onRowClick}
        />
      </div>
      <ul className="md:hidden">
        {visibles.map((f) => (
          <Fragment key={f.id}>{renderMobileCard(f)}</Fragment>
        ))}
      </ul>
      {total > PAGE_SIZE && (
        <div className="px-4">
          <PaginationBar page={actual} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </div>
      )}
    </div>
  )
}

/** A collapsed panel stays in the DOM under `hidden` so `aria-controls` always resolves. */
export function GroupedMatriz({
  filas,
  mode,
  areas,
  businessId,
  columns,
  rowTestId,
  onRowClick,
  noAceptablePrimero,
  renderMobileCard,
}: {
  filas: Fila[]
  mode: Agrupacion
  areas: {id: string; name: string}[]
  businessId: string
  columns: Column<Fila>[]
  rowTestId: (fila: Fila) => string
  onRowClick: (fila: Fila) => void
  noAceptablePrimero: boolean
  renderMobileCard: (fila: Fila) => ReactNode
}) {
  const {t} = useTranslation()
  const areaNameById = useMemo(() => new Map(areas.map((a) => [a.id, a.name])), [areas])
  const fallback = t(SIN_GRUPO[mode])
  const groups = useMemo(
    () => buildGroups(worstFirst(filas, noAceptablePrimero), mode, areaNameById, fallback),
    [filas, noAceptablePrimero, mode, areaNameById, fallback]
  )
  // Inside a group the order is always worst-first, so headers don't sort.
  const staticColumns = useMemo(() => columns.map((c) => ({...c, sortable: false})), [columns])
  const {collapsed, toggle} = useCollapsedState(`ipevr-grupos:${businessId}:${mode}`)
  const prefijoDePanel = useId()

  return (
    <div className="space-y-3" data-testid="ipevr-grouped">
      {groups.map((group, indice) => {
        const isCollapsed = collapsed.has(group.key)
        // La clave lleva el nombre del grupo, con espacios: `aria-controls` los leería como varios ids.
        const panelId = `${prefijoDePanel}-panel-${indice}`
        return (
          // Plegado, el canto rojo sigue diciendo qué grupo tiene riesgos no aceptables.
          <div
            key={group.key}
            className={cn(
              'surface overflow-hidden border-l-[3px]',
              group.noAceptables > 0 ? 'border-l-tone-red-fg' : 'border-l-primary'
            )}
            data-testid="ipevr-grupo">
            <button
              type="button"
              onClick={() => toggle(group.key)}
              aria-expanded={!isCollapsed}
              aria-controls={panelId}
              data-testid="ipevr-grupo-header"
              className={cn(
                'surface-divider flex w-full items-center gap-3 px-4 py-2.5 text-left outline-none hover:bg-secondary/50 focus-visible:bg-secondary/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset',
                // Plegado, la regla quedaría colgando contra el canto inferior.
                !isCollapsed && 'border-b'
              )}>
              <span aria-hidden="true" className="font-mono text-xs text-muted-foreground">
                {isCollapsed ? '▸' : '▾'}
              </span>
              <span
                className="min-w-0 flex-1 truncate font-mono text-[11px] font-medium tracking-wider text-foreground uppercase"
                data-testid="ipevr-grupo-nombre">
                {group.name}
              </span>
              <span className="flex shrink-0 items-center gap-1.5" data-testid="ipevr-grupo-subtotales">
                <Tag tone="neutral">{t('ipevr.agrupar.subtotal.filas', {count: group.filas.length})}</Tag>
                {group.noAceptables > 0 && (
                  <Tag tone="red">{t('ipevr.agrupar.subtotal.noAceptables', {count: group.noAceptables})}</Tag>
                )}
                <Tag tone="neutral">{t('ipevr.agrupar.subtotal.expuestos', {count: group.expuestos})}</Tag>
              </span>
            </button>
            <PanelDelGrupo
              panelId={panelId}
              hidden={isCollapsed}
              filas={group.filas}
              columns={staticColumns}
              rowTestId={rowTestId}
              onRowClick={onRowClick}
              renderMobileCard={renderMobileCard}
            />
          </div>
        )
      })}
    </div>
  )
}
